import type { CssGeneratorContext } from '../css-generator.model';

/**
 * Selectors of the red invalid border. The field primitives set aria-invalid="true" on their focusable element while
 * the error message shows; the select and multiselect draw the box around it. The date, phone, coordinates and choice
 * primitives keep the library's invalid colour. An element carrying the ng-* classes takes the border too; an F8 row
 * binds form-group--invalid-touched.
 */
export const INVALID_BORDER_SELECTORS = [
	'.ng-invalid.ng-touched',
	'.form-group--invalid-touched',
	'app-text-field [aria-invalid="true"]',
	'app-multiline-field [aria-invalid="true"]',
	'app-number-field [aria-invalid="true"]',
	'app-lookup [aria-invalid="true"]',
	'app-tree-select [aria-invalid="true"]',
	'app-select .p-select:has([aria-invalid="true"])',
	'app-multiselect .p-multiselect:has([aria-invalid="true"])',
] as const;

export const styles = ({ dt }: CssGeneratorContext) => `
p-inputnumber input.p-inputtext,
input.p-inputtext,
p-multiselect .p-multiselect-label,
p-treeselect .p-treeselect-label,
p-treeselect[size="large"] .p-treeselect-label,
span.p-select-label,
.treeselect-button span.p-select-label {
  @media screen and (min-width: 768px) and (max-width: 1439px) {
    font-size: ${dt('form.field.sm.font.size')} !important;
    line-height: ${dt('form.field.sm.font.lineHeight')} !important;
    padding: ${dt('form.field.sm.padding.y')} ${dt('form.field.sm.padding.x')};
  }

  @media screen and (min-width: 1440px) and (max-width: 1919px) {
    font-size: ${dt('form.field.md.font.size')} !important;
    line-height: ${dt('form.field.md.font.lineHeight')} !important;
    padding: ${dt('form.field.md.padding.y')} ${dt('form.field.md.padding.x')};
  }

  @media screen and (min-width: 1920px) {
    font-size: ${dt('form.field.lg.font.size')} !important;
    line-height: ${dt('form.field.lg.font.lineHeight')} !important;
    padding: ${dt('form.field.lg.padding.y')} ${dt('form.field.lg.padding.x')};
  }
}

${INVALID_BORDER_SELECTORS.join(',\n')} {
  border-color: ${dt('red.500')} !important;
}

/* Disabled inputs & textareas, app-wide. Browsers (WebKit especially) dim
   disabled form controls via -webkit-text-fill-color and reduced opacity, both of
   which override the plain color property. Pin color, text-fill, background and
   opacity to the design tokens so every disabled field stays readable. */
.p-inputtext:disabled,
textarea.p-textarea:disabled {
    color: ${dt('form.field.disabled.color')} !important;
    -webkit-text-fill-color: ${dt('form.field.disabled.color')} !important;
    background-color: ${dt('form.field.disabled.background')} !important;
    border-color: ${dt('form.field.disabled.background')} !important;
    opacity: 1 !important;
}

.p-multiselect:has(.p-multiselect-clear-icon) .p-multiselect-label {
    padding-inline-end: calc(1rem + ${dt('select.padding.x')});
}

/* A leading field icon (iconValue) on the select, multiselect and tree select: the library icon field insets the text of
   a .p-inputtext after a leading icon, and the select boxes carry their text in a label (and the tree select its chips
   in a chip lane), so these get the same inset as an icon-led text field. */
.p-iconfield > .p-inputicon:first-child ~ * .p-select-label,
.p-iconfield > .p-inputicon:first-child ~ * .p-multiselect-label,
.p-iconfield > .p-inputicon:first-child ~ * .treeselect-button .flex.flex-wrap {
    padding-inline-start: calc((${dt('form.field.padding.x')} * 2) + ${dt('icon.size')});
}

.custom-focus-styles {
  & .p-inputtext{
    font-weight: 400;
   }

  & .p-inputtext.p-component:focus,
   & p-autocomplete div.p-inputwrapper-focus,
   & p-autocomplete.ng-dirty div {
    color: ${dt('named.ink')};
    }

    /* The class sits on the field host (app-text-field, app-phone-input, app-address-form), whose
       data-dirty attribute marks a value the user edited; an edited field keeps the input colour. */
    &:not([data-dirty]) .p-inputtext.p-component:not(:focus):not(.ng-dirty),
    &:not([data-dirty]) p-autocomplete:not(.ng-dirty) div:not(.p-inputwrapper-focus){
      color: ${dt('surface.960')};
    }
}

span.p-tag-label {
  padding: 0px;
  white-space: nowrap;
}

p-select span {
  padding: 0px;
}

@media screen and (min-width: 768px) {
  .wide-filter .p-inputtext, .wide-filter app-activities-multiselect, .wide-filter p-select, .wide-filter app-clsf-sanction-multiselect{
    min-width: 23.875rem;
  }
}

/* The equal max-width fixes the tree select's width, so a selected chip wraps inside the trigger instead of widening it. */
.wide-filter app-clsf-sanction-multiselect {
  max-width: 23.875rem;
}

.create-form app-text-field, .create-form app-phone-input, .create-form app-activities-multiselect, .create-form p-select, .create-form p-autocomplete {
  max-width: 26.5rem;
}

/* Wide input column (Figma ~818px content). Pair with .create-form on <form>. */
.input-form-fields {
  width: 100%;
  max-width: 51.125rem;
}

.input-form-fields.create-form app-text-field,
.input-form-fields.create-form app-activities-multiselect,
.input-form-fields.create-form p-select,
.input-form-fields.create-form p-autocomplete,
.input-form-fields.create-form p-treeselect {
  max-width: 51.125rem;
}

.create-form-row .p-inputtext, .create-form-row p-select, .create-form-row app-text-field, .create-form-row app-phone-input {
  min-width: 12.5rem;
}

/* Mercury-style: the country flag/caret sits INSIDE the number field, so there is exactly
   one box and one height. The field fills its width and is padded on the left to clear the
   overlaid trigger. */
app-phone-input p-inputmask,
app-phone-input p-inputmask .p-inputtext {
  width: 100%;
}

app-phone-input p-inputmask .p-inputtext {
  /* 3.5rem flag area (= the trigger width / divider position) + the field's own
     horizontal padding, so the number text lines up with other fields' text. */
  padding-left: calc(3.5rem + ${dt('form.field.padding.x')});

  @media screen and (min-width: 768px) and (max-width: 1439px) {
    padding-left: calc(3.5rem + ${dt('form.field.sm.padding.x')});
  }

  @media screen and (min-width: 1440px) and (max-width: 1919px) {
    padding-left: calc(3.5rem + ${dt('form.field.md.padding.x')});
  }

  @media screen and (min-width: 1920px) {
    padding-left: calc(3.5rem + ${dt('form.field.lg.padding.x')});
  }
}

app-phone-input .phone-country-trigger {
  border-inline-end: 1px solid ${dt('form.field.border.color')};
}

/* Country picker popover: drop the inner padding and the listbox's own border so the
   popover is the only visible box (no box-in-a-box). */
.phone-country-popover .p-popover-content {
  padding: 0;
}

.phone-country-popover .p-listbox {
  border: none;
  background: transparent;
}

.phone-country-popover .p-listbox-option-group {
  font-size: 0.875rem;
}
`;
